import { Checkbox, CheckboxGroup, Popover, PopoverContent, PopoverTrigger, Separator } from '@ibsys/kit';
import { FilterIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DIALOG_EVENT_FILTERS, type DialogEventFilter } from '@/entities/conversations';
import { IconButton } from '@/shared/ui';

type Props = {
  value: DialogEventFilter[];
  onChange: (value: DialogEventFilter[]) => void;
  /** Доступна ли у диалога неанонимизированная версия — управляет видимостью тумблера показа без анонимизации */
  canShowNonAnonymized?: boolean;
  /** Текущее состояние показа транскрипта без анонимизации */
  showNonAnonymized?: boolean;
  /** Показ без анонимизации нельзя включить: идёт правка анонимизированного текста */
  isShowNonAnonymizedDisabled?: boolean;
  /** Обработчик переключения показа без анонимизации */
  onShowNonAnonymizedChange?: (value: boolean) => void;
};

const INTERRUPTION_FILTERS: DialogEventFilter[] = ['interruption', 'interruption_client', 'interruption_employee'];

/** Названия событий в окне отбора — по одному на каждое событие общего перечня */
const EVENT_LABEL_KEYS: Record<DialogEventFilter, string> = {
  pause: 'dialogs:filters.pause',
  interruption: 'dialogs:filters.interruption',
  interruption_client: 'dialogs:filters.interruptionClient',
  interruption_employee: 'dialogs:filters.interruptionEmployee',
};

export const TranscriptEventFilters: FC<Props> = ({
  value,
  onChange,
  canShowNonAnonymized,
  showNonAnonymized,
  isShowNonAnonymizedDisabled,
  onShowNonAnonymizedChange,
}) => {
  const { t } = useTranslation(['dialogs']);

  const options = useMemo(
    () => DIALOG_EVENT_FILTERS.map((value) => ({ value, label: t(EVENT_LABEL_KEYS[value] as never) })),
    [t],
  );

  const handleChange = (selectedValues: string[]) => {
    const next = selectedValues as DialogEventFilter[];
    // Эксклюзивность interruption-опций (radio-поведение):
    // если пользователь добавил один из вариантов перебивания — снимаем остальные два.
    const previouslySelectedInterruption = value.filter((v) => INTERRUPTION_FILTERS.includes(v));
    const currentlySelectedInterruption = next.filter((v) => INTERRUPTION_FILTERS.includes(v));
    const added = currentlySelectedInterruption.find((v) => !previouslySelectedInterruption.includes(v));

    if (added) {
      const filtered = next.filter((v) => !INTERRUPTION_FILTERS.includes(v) || v === added);
      onChange(filtered);
      return;
    }

    onChange(next);
  };

  return (
    <Popover>
      <IconButton
        size="icon"
        label={t('dialogs:filters.title')}
        icon={<FilterIcon className="size-4" />}
        trigger={PopoverTrigger}
      />
      <PopoverContent className="w-56" align="end">
        <div className="flex flex-col gap-3">
          <CheckboxGroup value={value} onChange={handleChange} options={options} className="flex flex-col gap-2" />
          {/* Чекбокс показа без анонимизации — как остальные пункты меню; виден только когда у диалога есть неанонимизированная версия */}
          {canShowNonAnonymized && onShowNonAnonymizedChange && (
            <>
              <Separator />
              <Checkbox
                value={showNonAnonymized ?? false}
                disabled={isShowNonAnonymizedDisabled}
                onChange={(checked) => onShowNonAnonymizedChange(checked === true)}
                label={t('dialogs:showNonAnonymized')}
              />
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
